import { ActionIcon, Checkbox, NumberInput, TextInput } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import classnames from 'classnames';
import { useEffect, useState } from 'react';

import { variationPercent } from 'src/Utils/budgetWizardMath';

import styles from './BudgetSuggestionRow.module.scss';
import { Props } from './BudgetSuggestionRow.types';

/** Même borne que la colonne en base : la note est une annotation, pas un commentaire. */
const NOTE_MAX_LENGTH = 120;

const BudgetSuggestionRow = ({
  checked,
  onCheckedChange,
  title,
  meta,
  amountLabel,
  amount,
  onAmountChange,
  previousYearAmount,
  percentLabel,
  onPercentChange,
  children,
  onRemove,
  removeLabel,
  note,
  noteLabel,
  notePlaceholder,
  onNoteChange,
}: Props) => {
  const showPercent = previousYearAmount !== undefined && !!onPercentChange;
  const derivedPercent = variationPercent(amount, previousYearAmount);

  // The percent field is bidirectional: it is derived from the amount, but typing in it
  // recomputes the amount. Committing on blur (and not on every keystroke) is what keeps the
  // two from fighting — the draft is resynced from the derived value once the amount moved.
  const [percentDraft, setPercentDraft] = useState<string | number>(derivedPercent ?? '');
  useEffect(() => {
    setPercentDraft(derivedPercent ?? '');
  }, [derivedPercent]);

  const commitPercent = () => {
    if (!onPercentChange) return;
    const parsed = typeof percentDraft === 'number' ? percentDraft : parseFloat(percentDraft);
    if (!Number.isFinite(parsed)) {
      setPercentDraft(derivedPercent ?? '');
      return;
    }
    onPercentChange(parsed);
  };

  return (
    <div className={classnames(styles.container, { [styles.checked]: checked })}>
      <Checkbox
        className={styles.checkbox}
        size="md"
        checked={checked}
        onChange={event => onCheckedChange(event.currentTarget.checked)}
        aria-label={title}
      />

      <div className={styles.info}>
        <div className={styles.title}>{title}</div>
        {meta ? <div className={styles.meta}>{meta}</div> : null}
      </div>

      <div className={styles.extras}>
        {children}

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{amountLabel}</span>
          <NumberInput
            className={styles.amountInput}
            size="md"
            value={amount}
            onChange={value => {
              const parsed = Number(value);
              onAmountChange(Number.isFinite(parsed) ? parsed : 0);
            }}
            min={0}
            suffix=" €"
            thousandSeparator=" "
            hideControls
          />
        </label>

        {onNoteChange ? (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{noteLabel}</span>
            <TextInput
              className={styles.noteInput}
              size="md"
              value={note ?? ''}
              onChange={event => onNoteChange(event.currentTarget.value)}
              placeholder={notePlaceholder}
              maxLength={NOTE_MAX_LENGTH}
            />
          </label>
        ) : null}

        {showPercent ? (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{percentLabel}</span>
            <NumberInput
              className={styles.percentInput}
              size="md"
              value={percentDraft}
              onChange={setPercentDraft}
              onBlur={commitPercent}
              disabled={derivedPercent === null && !previousYearAmount}
              placeholder="—"
              suffix=" %"
              decimalScale={1}
              hideControls
            />
          </label>
        ) : null}

        {onRemove ? (
          <ActionIcon
            className={styles.remove}
            variant="subtle"
            color="red"
            size="lg"
            onClick={onRemove}
            aria-label={removeLabel}
          >
            <IconTrash size={20} />
          </ActionIcon>
        ) : null}
      </div>
    </div>
  );
};

export default BudgetSuggestionRow;
